import type {FieldValue, VehicleTypeEnum} from '../sharedTypes';
import type {AdditionalInformation} from './AdditionalInformation';
import type {Dimensions} from './Dimensions';
import type {ElectricVehicleBatteryResponseBody} from './ElectricVehicleBatteryResponseBody';
import type {Engine} from './Engine';
import type {FullState} from './FullState';
import type {ModelSpecification} from './ModelSpecification';
import type {SaleStateEnum} from './SaleStateEnum';
import type {SaleTypeEnum} from './SaleTypeEnum';
import type {VehicleAlbum} from './VehicleAlbum';
import type {VehicleDataResponseBody} from './VehicleDataResponseBody';
import type {VehicleFeature} from './VehicleFeature';
import type {VehiclePhoto} from './VehiclePhoto';
import type {VehiclePrice} from './VehiclePrice';

export type FullVehicle = {
  id: string;
  externalId?: string | null;
  branchId: string;
  publicId?: string | null;
  dimensions?: Dimensions | null;
  engine?: Engine | null;
  ownerships?: any[]; // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
  modelSpecification?: ModelSpecification | null;
  state?: FullState | null;
  additionalInformation?: AdditionalInformation | null;
  features?: VehicleFeature[];
  photos?: VehiclePhoto[];
  albums?: VehicleAlbum[];
  /**
   * Vehicle VIN identificator
   */
  vin?: string | null;
  /**
   * Vehicle manufacturer name
   */
  make: string;
  /**
   * Vehicle model family name
   */
  modelFamily: string;
  /**
   * Vehicle trim level, often specifying motorisation or equipment
   */
  trim?: string | null;
  /**
   * Vehicle variant name, such as editions, limited variants or specific types
   */
  variant?: string | null;
  type?: VehicleTypeEnum;
  /**
   * Vehicle fuel type
   */
  fuelType?: string | null;
  /**
   * Vehicle secondary fuel type
   */
  secondaryFuelType?: string | null;
  /**
   * Vehicle hand drive type
   */
  handDriveType?: string | null;
  /**
   * Vehicle transmission type
   */
  transmission?: string | null;
  /**
   * Vehicle category by door count and layout
   */
  doorCountCategory?: string | null;
  /**
   * Vehicle body style
   */
  bodyStyle?: string | null;
  /**
   * Vehicle drive specification
   */
  drive?: string | null;
  /**
   * Vehicle power in kW
   */
  power?: number | null;
  firstRegistrationOnDay?: number | null;
  firstRegistrationOnMonth?: number | null;
  firstRegistrationOnYear?: number | null;
  manufacturedOnMonth?: number | null;
  manufacturedOnYear?: number | null;
  saleState: SaleStateEnum | null;
  saleType: SaleTypeEnum | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  isArchived?: boolean | null;
  sellingPrice: VehiclePrice;
  purchasePrice: VehiclePrice;
  customMake?: string | null;
  customModelFamily?: string | null;
  isVatDeductible?: boolean | null;
  vehicle: VehicleDataResponseBody;
  /**
   * Vehicle title, if null in DB, then value will be resolved from make + model + trim + variant.
   */
  title: string;
  /**
   * Vehicle keys physical location description.
   */
  keyLocation?: string | null;
  participationId: string;
  featuresByManufacturer?: string | null;
  registrationPlate?: string | null;
  vehicleSourceCodeId?: string | null;
  axleCount?: number | null;
  customFields?: FieldValue[];
  electricVehicleBattery?: ElectricVehicleBatteryResponseBody | null;
};
